import React, { useRef, useEffect } from 'react'
import { View, StyleSheet, TouchableOpacity, Animated, ActivityIndicator } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Text } from '../atoms'
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder'
import { useFtVoiceNote } from '../../hooks/api/mutations/ft-voice-note/useFtVoiceNote'
import { useLanguage } from '../../context'

interface VoiceRecordButtonProps {
  profileId: string
}

export const VoiceRecordButton: React.FC<VoiceRecordButtonProps> = ({ profileId }) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()
  const { isRecording, startRecording, stopRecording, duration } = useVoiceRecorder()
  const { mutateAsync, isPending } = useFtVoiceNote()
  const pulseAnim = useRef(new Animated.Value(1)).current

  useEffect(() => {
    if (isRecording) {
      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, { toValue: 1.2, duration: 600, useNativeDriver: true }),
          Animated.timing(pulseAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
        ]),
      )
      animation.start()
      return () => animation.stop()
    } else {
      pulseAnim.setValue(1)
    }
  }, [isRecording, pulseAnim])

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const handlePress = async () => {
    if (isPending) return

    if (isRecording) {
      const audioResult = await stopRecording()
      await mutateAsync({ profileId, audioResult })
    } else {
      await startRecording()
    }
  }

  if (isPending) {
    return (
      <View style={styles.container}>
        <View style={[styles.button, styles.processingButton]}>
          <ActivityIndicator size="small" color={theme.colors.white} />
        </View>
        <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>...</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
        <TouchableOpacity
          onPress={handlePress}
          style={[styles.button, isRecording && styles.recordingButton]}
        >
          <Icon
            name="microphone"
            size={theme.iconSize.small}
            color={isRecording ? theme.colors.white : theme.colors.primary}
          />
        </TouchableOpacity>
      </Animated.View>
      {isRecording ? (
        <Text type="bodyExtraSmall" color={theme.colors.error}>{formatDuration(duration)}</Text>
      ) : (
        <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>{t('voiceNote.vocal')}</Text>
      )}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      gap: 4,
    },
    button: {
      width: 44,
      height: 44,
      borderRadius: 22,
      borderWidth: 1,
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.white,
      justifyContent: 'center',
      alignItems: 'center',
    },
    recordingButton: {
      backgroundColor: theme.colors.error,
      borderColor: theme.colors.error,
    },
    processingButton: {
      backgroundColor: theme.colors.greyBlue,
      borderColor: theme.colors.greyBlue,
    },
  })
